// /////////////////////////////////////////////////////////////
// [ファイル] sourceQualitySample.ts
// [概要] ソース品質管理プラットフォーム フェーズ1 (MVP) 用のサンプルデータ。
//        UI / 操作感を確認するための固定データ。バックエンド (パーサ →
//        Firestore) 配線後は useSourceQuality 内でこのデータを実取得に
//        差し替える。構造は本番データモデル (sourceQuality.ts) と一致。
// /////////////////////////////////////////////////////////////
import { SourceModule } from '../types/sourceQuality';

export const SAMPLE_ASSIGNEES = ['（未割当）', '平良', '田中', '佐藤', '鈴木'];

export const SOURCE_QUALITY_SAMPLE: SourceModule[] = [
  {
    module_id: 'm-use-project-table',
    file_path: 'src/hooks/useProjectTable.ts',
    language: 'ts',
    project: 'frontend',
    symbols: [
      {
        symbol_id: 's-use-project-table',
        module_id: 'm-use-project-table',
        file_path: 'src/hooks/useProjectTable.ts',
        qualified_name: 'useProjectTable',
        name: 'useProjectTable',
        kind: 'hook',
        language: 'ts',
        signature: '(initialFilters?: ProjectFilters) => ProjectTableState',
        code_snapshot: `export const useProjectTable = (initialFilters?: ProjectFilters) => {
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(initialFilters ?? {});
  const lastRequestId = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++lastRequestId.current;
    const res = await axios.post(ep('get_salesforce_data'), { page, filters });
    if (requestId !== lastRequestId.current) return; // stale 応答を破棄
    setRows(res.data.rows);
  }, [page, filters]);

  return { rows, page, setPage, filters, setFilters, refresh: fetchData };
};`,
        ai_summary:
          'SFA 案件一覧のページネーション・フィルタ状態を管理するフック。`lastRequestId` で stale な非同期応答を破棄しており、連続検索時の競合に配慮されている。',
        complexity: 4,
        review_status: 'reviewed',
        assignee: '平良',
        tags: ['hook', 'sfa', 'pagination'],
        issues: [],
        ai_comments: [
          {
            id: 'c1',
            type: 'summary',
            body: '責務は単一でテスト容易。fetchData の依存配列は適切。',
            created_at: '2026-06-26',
          },
        ],
      },
    ],
  },
  {
    module_id: 'm-standard-data-table',
    file_path: 'src/components/StandardDataTable.tsx',
    language: 'tsx',
    project: 'frontend',
    symbols: [
      {
        symbol_id: 's-standard-data-table',
        module_id: 'm-standard-data-table',
        file_path: 'src/components/StandardDataTable.tsx',
        qualified_name: 'StandardDataTable',
        name: 'StandardDataTable',
        kind: 'react_component',
        language: 'tsx',
        signature: '(props: StandardDataTableProps) => JSX.Element',
        code_snapshot: `export default function StandardDataTable({
  rows, columns, loading, totalCount, page, pageSize,
  onPageChange, onPageSizeChange,
}: StandardDataTableProps) {
  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      {loading && <LoadingView message="読み込み中" />}
      <TableContainer>
        {/* 固定ヘッダ + sticky 列 */}
      </TableContainer>
      <Pagination count={totalCount} page={page} onChange={onPageChange} />
    </Box>
  );
}`,
        ai_summary:
          '一覧画面で再利用される共通テーブル。読み込みオーバーレイ・ページングを内包する。props が多めで、将来は columns 定義のメモ化推奨。',
        complexity: 6,
        review_status: 'in_review',
        assignee: '田中',
        tags: ['component', 'shared', 'table'],
        issues: [
          {
            id: 'i1',
            title: 'columns を親で毎レンダリング生成すると再描画が重い',
            body: 'columns を useMemo 化するか、モジュールスコープに固定したい。',
            severity: 'medium',
            status: 'open',
            created_by: '田中',
            created_at: '2026-06-25',
          },
        ],
        ai_comments: [],
      },
    ],
  },
  {
    module_id: 'm-sfa-search',
    file_path: 'functions-python/services/sfa_search.py',
    language: 'python',
    project: 'functions-python',
    symbols: [
      {
        symbol_id: 's-get-sales-project-detail',
        module_id: 'm-sfa-search',
        file_path: 'functions-python/services/sfa_search.py',
        qualified_name: 'get_sales_project_detail',
        name: 'get_sales_project_detail',
        kind: 'function',
        language: 'python',
        signature: 'get_sales_project_detail(project_id: str) -> dict',
        code_snapshot: `def get_sales_project_detail(project_id: str) -> dict:
    engine = DatabaseConnection.get_engine()
    query = sqlalchemy.text("""
        SELECT p.*, p.order_confirmed_date::text AS order_confirmed_date
        FROM trn_sfa_projects p
        WHERE p.project_id = :pid
    """)
    with engine.connect() as conn:
        row = conn.execute(query, {"pid": project_id}).mappings().first()
    return dict(row) if row else {}`,
        ai_summary:
          'SFA 案件詳細を 1 件取得する。日付カラムを SQL 側で ::text にキャストしており、JSON シリアライズ失敗 (date 型) を回避する規約に準拠している。',
        complexity: 3,
        review_status: 'reviewed',
        assignee: '佐藤',
        tags: ['sfa', 'query', 'cloud-run'],
        issues: [],
        ai_comments: [
          {
            id: 'c2',
            type: 'quality',
            body: '::text キャスト規約を遵守。example の bindparam 利用で SQL インジェクション安全。',
            created_at: '2026-06-26',
          },
        ],
      },
      {
        symbol_id: 's-update-sales-project',
        module_id: 'm-sfa-search',
        file_path: 'functions-python/services/sfa_search.py',
        qualified_name: 'update_sales_project',
        name: 'update_sales_project',
        kind: 'function',
        language: 'python',
        signature: 'update_sales_project(project_id: str, payload: dict) -> dict',
        code_snapshot: `def update_sales_project(project_id: str, payload: dict) -> dict:
    engine = DatabaseConnection.get_engine()
    columns = ", ".join(f"{k} = :{k}" for k in payload)
    query = sqlalchemy.text(f"UPDATE trn_sfa_projects SET {columns} WHERE project_id = :pid")
    with engine.begin() as conn:
        conn.execute(query, {**payload, "pid": project_id})
    return {"status": "success"}`,
        ai_summary:
          '案件を更新する。payload のキーをそのまま SQL 列名に展開しているため、許可カラムのホワイトリスト検証が無いと予期せぬ列更新のリスクがある。',
        complexity: 5,
        review_status: 'needs_fix',
        assignee: '鈴木',
        tags: ['sfa', 'write', 'security'],
        issues: [
          {
            id: 'i2',
            title: 'payload キーを列名に直接展開している',
            body: '許可カラムのホワイトリストで検証してから UPDATE 文を組み立てるべき。',
            severity: 'high',
            status: 'open',
            created_by: '鈴木',
            created_at: '2026-06-24',
          },
        ],
        ai_comments: [
          {
            id: 'c3',
            type: 'suggestion',
            body: 'ALLOWED_COLUMNS の集合で payload を絞り込むワンクッションを推奨。',
            created_at: '2026-06-26',
          },
        ],
      },
    ],
  },
  {
    module_id: 'm-ai-flows',
    file_path: 'functions-node/src/ai_flows.ts',
    language: 'ts',
    project: 'functions-node',
    symbols: [
      {
        symbol_id: 's-analyze-symbol-flow',
        module_id: 'm-ai-flows',
        file_path: 'functions-node/src/ai_flows.ts',
        qualified_name: 'analyzeSymbolFlow',
        name: 'analyzeSymbolFlow',
        kind: 'const',
        language: 'ts',
        signature: 'Flow<AnalyzeInput, AnalyzeOutput>',
        code_snapshot: `export const analyzeSymbolFlow = ai.defineFlow(
  {
    name: 'analyzeSymbolFlow',
    inputSchema: z.object({ language: z.string(), code: z.string() }),
    outputSchema: z.object({
      ai_summary: z.string(),
      issues_json: z.string(), // 任意 JSON 配列は 400 になるため文字列で受ける
    }),
  },
  async (input) => {
    const { output } = await ai.generate({ prompt: buildPrompt(input) });
    return output;
  },
);`,
        ai_summary:
          'シンボルのコードを Gemini に渡して要約・課題候補を生成する Genkit フロー。issues を z.string() で受ける既知の回避策に従っており、関数宣言 400 エラーを防いでいる。',
        complexity: 4,
        review_status: 'unreviewed',
        assignee: null,
        tags: ['ai', 'genkit'],
        issues: [],
        ai_comments: [],
      },
    ],
  },
];
